import makeId from "../../helpers/make-id.js";
import store from "../../init/store.js";
import {initFirebaseBattleBindings} from "./firebase.js";
import {setMatchmakingValue, setMatchmakingError} from "../../redux-actions/battle-actions.js";
import {getRandomLoadout} from "../../Fixture_combatants.js";
import {getPlayerLoadout} from '../PlayerFixture_combatant.js'
import {modelsFromObject} from "../../helpers/models-from-object.js";
import CombatantModel from '../models/CombatantModel.js'
import {setBattleValue} from '../../redux-actions/battle-actions.js'
import {addTeamReferencesAndIdsToCombatants} from '../lifecycle/team-utilities.js'

/** Decide which room to connect to */
export async function pairPlayerWithRoom() {

  //
  const playerCombatantData = getPlayerLoadout();

  //Case 1: I have a room in mind (through the URL) and I want to join that one:
  const redirectToRoom = store.getState().matchmaking.redirectToRoom;
  if (redirectToRoom) {
    const validationResult = await validateThatRoomIsValid(redirectToRoom);

    if (validationResult.type === "Error") {
      //Maybe throw an error if we have one.

      //Show an error if this is the full one
      if (
        validationResult.code === "error/full" ||
        validationResult.code === "error/started"
      ) {

        //Originally, all of these Error cases would show errors. They aren't really errors though,
        //But, removing this logic broke stuff. So only show this one possible error

        setMatchmakingError({ //requires MatchmakingErrorMessage type
          status: "Error",
          code: validationResult.code,
          message: validationResult.message
        });
      }

      if (validationResult.code === "error/doesnt-exist") {
        //maybe we should just connect them to the desired ID? "redirectToRoom"
        //Yes.. then other people can still join the same stale link
        const newRoomId = await createNewRoom(redirectToRoom);
        connectToRoom(newRoomId, playerCombatantData);
      }

      if (validationResult.code === "error/duplicate-entry") {
        //Force to "NOPE" screen. This user already is active in this room.
        setMatchmakingValue({ //requires MatchmakingErrorMessage type
          isDuplicateEntryScreen: true
        });
        return;
      }
    }


    if (validationResult.type === "Success") {

      //Check if we are RECONNECTING to this room
      if (validationResult.code === "success/rejoining-room") {
        rejoinRoom(redirectToRoom, playerCombatantData);
        return;
      }

      //Go ahead and connect to that room.
      //console.log('found the desired room', redirectToRoom);
      connectToRoom(redirectToRoom, playerCombatantData);
    }


  } else {
    //Case 2: I don't have a room in mind. Just put me in one.
    const newRoomId = await createNewRoom();
    // console.log('newRoom', newRoomId);
    connectToRoom(newRoomId, playerCombatantData);
  }
}

/** Check this roomID to see if there is room for me */
export async function validateThatRoomIsValid(roomId: string) {

  const desiredRoomSnapshot = await window.firebase.database().ref(`rooms/${roomId}`).once("value");
  const desiredRoom = desiredRoomSnapshot.val();

  const myPlayerId = store.getState().battle.battlePlayerId[0];

  //possible fail: this room doesn't exist
  if (!desiredRoom) {
    return {
      type: "Error",
      code: "error/doesnt-exist",
      message: "Room does not exist. Creating a new one!"
    }
  }

  //possible fail: too many connections in FULLTEAM BATTLE room. (check connections for max of 2 human players)
  if (desiredRoom.roomType === "fullteam" &&
    desiredRoom.connections &&
    Object.keys(desiredRoom.connections).length >= 2) {
    return {
      type: "Error",
      code: "error/full",
      message: "Full Team battle already has two players."
    }
  }

  //possible fail: too many connections in OPEN BATTLE room. (now checking initialCombatantData instead, which includes Bots)
    if (
      desiredRoom.initialCombatantData && //combatants exist inside here
      !desiredRoom.initialCombatantData[myPlayerId] && //I'm not a founding member of the room (in other words, allow founder to rejoin)
      Object.keys(desiredRoom.initialCombatantData).length >= 6 //Room isn't full
    ) {
      return {
        type: "Error",
        code: "error/full",
        message: "Room is full."
      }
    }





  //possible fail: You're already in this room.
  if (desiredRoom.connections && desiredRoom.connections[myPlayerId]) {
    return {
      type: "Error",
      code: "error/duplicate-entry",
      message: "You are already in this room."
    }
  }


  //possible fail: this battle has already started
  if (desiredRoom.hasBattleStarted) {

    //First, check if we've been in this room as a combatant before (Rejoining)
    //If we've been here before, our battlePlayerId will be listed in the room's BOTS object
    if (desiredRoom.bots && desiredRoom.bots[myPlayerId]) {
      //Valid to rejoin this room!
      //NOTE: THIS RESPONSE IS THE ABSOLUTE GATEKEEPER FOR REJOINING.
      //If shit hits the fan and Rejoining needs to be turned off, turn this feature flag OFF
      if (store.getState().battle.ff_IsRejoiningOn) {
        return {
          type: "Success",
          code: "success/rejoining-room",
        }
      } else {
        return {
          type: "Error",
          code: "error/started",
          message: "Battle has already started. Rejoining is not available at this time!"
        }
      }
    }

    return {
      type: "Error",
      code: "error/started",
      message: "Battle has already started!"
    }
  }

  //Success!
  return {
    type: "Success", //Note: this pre-dates the Flow type with "Success" as a status.
    code: "success/validate-room",
  }
}

/** Creates a new room and return the ID so it can be connected to. */
export async function createNewRoom(desiredId: any) {

  //TODO: Should `desiredId` be validated? Don't want people writing naughty things

  //Establish the new room's Id.
  const newRoomId = desiredId || makeId("", 7).replace("_", "");

  //Set the values in the new room
  const myId = store.getState().battle.battlePlayerId[0];
  await window.firebase.database().ref(`rooms/${newRoomId}`).set({
    hasBattleStarted: false,
    createdTimestamp: Date.now(),
    createdByClientId: myId, //This will never change. This marks who created the room. It helps with knowing acquisition for people who get invited here
    roomType: store.getState().matchmaking.createRoomType, //"fullteam" or "open"
    masterCombatantId: myId, //Set master to my playerID (from Firebase login) This value can CHANGE if the master leaves!
    initialCombatantData: {}, //Note: firebase won't write these. Just a reminder that they start fresh
    initialTeamData: {} //Note: firebase won't write these. Just a reminder that they start fresh
  });

  //When new room is written, return this value.
  return newRoomId;
}


/** Set our global variable then connect */
export function connectToRoom(roomId: string) {

  window.roomRef = window.firebase.database().ref(`rooms/${roomId}`);
  const battlePlayerId = store.getState().battle.battlePlayerId;

  //TODO: make sure crew.members always has the loadouts merged in (attacks, items, etc)
  const crewMembers = store.getState().crew.members;
  const mainMember = crewMembers[ Object.keys(crewMembers)[0] ];
  const memberTwo = crewMembers[ Object.keys(crewMembers)[1] ];
  const memberThree = crewMembers[ Object.keys(crewMembers)[2] ];


  window.roomRef.child('roomType').once("value", snap => {

    const roomType = snap.val();
    setMatchmakingValue({
      currentRoomId: roomId,
      currentRoomType: roomType
    });


    //Set combatantData
    window.roomRef
      .child("initialCombatantData")
      .child(battlePlayerId[0])
      .set(mainMember);

    if (roomType === "fullteam") {
      //Create first buddy
      if (memberTwo) {
        addBuddy(memberTwo);
      }

      //Create second buddy
      if (memberThree) {
        addBuddy(memberThree);
      }
    }

    //Add crew name
    updateCrewNameInRoom();


    //Init firebase listeners!
    initFirebaseBattleBindings();

    //[Side Effect] at this point, go ahead and scan firebase for old rooms. It's okay to delete them.
    getStaleRoomsFromFirebase([roomId], staleRoomsIdArray => {
      deleteRoomsFromFirebase(staleRoomsIdArray)
    });
  });
}

export function setInitialCombatantData(id:string, characterData:{}, callback:Function) {
  //Sets it in "initialCombatantData" node. (for use in setting up a non-started battle)

  window.roomRef
    .child("initialCombatantData")
    .child(id)
    .update(characterData).then(callback);
}


/** Set our global variable then rejoin the room (copy of connectToRoom, but with some specifics for rejoining) */
export function rejoinRoom(roomId: string, characterData: Object) {

  window.roomRef = window.firebase.database().ref(`rooms/${roomId}`);

  //Get the 'rejoiner' combatants and then connect
  window.roomRef.child('rejoinCombatantState').once("value", combatantsSnap => {


    //Get the teams, too
    window.roomRef.child('rejoinTeams').once("value", teamsSnap => {


      const rejoinerCombatants = addTeamReferencesAndIdsToCombatants(combatantsSnap.val(), teamsSnap.val());
      //console.log('rejoin!', rejoinerCombatants);

      //Rectify my local state of combatants/teams before actually rejoining the battle
      //NOTE: copied from `battleState/isBattleInit` tree in firebase.js
      //Set the combatants
      let combatantModels = {};
      Object.keys(rejoinerCombatants).forEach(key => {
        combatantModels[key] = {
          ...new CombatantModel(rejoinerCombatants[key]),
        }
      });

      setBattleValue({
        combatants: combatantModels, //set the combatants
        teams: teamsSnap.val(), //and the teams
      });
      //////--------------------------------------------------

      //Set this id to local state so I know what it is
      setMatchmakingValue({
        currentRoomId: roomId
      });


      //When rejoining, dont forget to set the roomtype
      window.roomRef.child('roomType').once("value", snap => {
        const roomType = snap.val();
        setMatchmakingValue({
          currentRoomId: roomId,
          currentRoomType: roomType
        });

        initFirebaseBattleBindings();

      });


    });
  });

}

/* Buddy */
export function addBuddy(characterData:Object) {

  const battlePlayerId = store.getState().battle.battlePlayerId; //[]

  const buddyId = makeId("buddy", 10);
  const buddyLoadout = {
    //...getRandomLoadout(),
    ...characterData,
    isBuddy: true, //This lets us auto mark the "viewed"
    belongsToClient: battlePlayerId[0] //link to my client so we can team match later
  };

  //Mark me as controlling that buddy.
  setBattleValue({
    battlePlayerId: [
      ...battlePlayerId,
      buddyId
    ]
  });

  //Send that buddy up to Firebase
  window.roomRef
    .child("initialCombatantData")
    .child(buddyId)
    .set(buddyLoadout);
}



/** Add a bot to the room */
export function addBot(botData: Object) {
  const botId = makeId("bot", 10);

  botData = botData || getNewBotData();

  window.roomRef.child("initialCombatantData").child(botId).set(botData);
}

//Just get bot data
export function getNewBotData() {
  return {
    ...getRandomLoadout(),
    isBot: true,
    //TODO: take this out. For testing, make bots stupid fast (or slow)
    // hp: 1,
    //speedModifier: -9
  };
}


/** Remove a bot from the room */
export function removeBot(botId: string) {
  window.roomRef.child("initialCombatantData").child(botId).remove();
}


/** Get rooms that can be deleted. Exclude the roomId (or more in the future? It's an array) that the player is connected to  */
export function getStaleRoomsFromFirebase(excludeIds: Array<string>, callback: Function) {

  let roomsRef = window.firebase.database().ref(`rooms`);
  roomsRef.once("value", function (snapshot) {
    const rooms = snapshot.val() || {};

    //let destroyIdsList = [];
    const now = Date.now();


    const destroyIdsList = modelsFromObject(rooms).filter(roomModel => {
      return isRoomOkayToDelete(roomModel, excludeIds, now)
    }).map(roomModel => roomModel._id);

    callback(destroyIdsList);
  });
}

function isRoomOkayToDelete(roomModel, excludeIds, now) {

  //Do not delete if we're excluding this id
  if (excludeIds.includes(roomModel._id)) {
    return false
  }

  //Delete if created more than 2 hours ago (7200000ms))
  if (now - roomModel.createdTimestamp > 7200000) {
    return true
  }

  //Delete if we have no more connections
  return (!roomModel.connections)
}



/** Take in a list of roomIds and destroy them all */
export function deleteRoomsFromFirebase(roomIdsToDestroy: Array<string>) {
  roomIdsToDestroy.forEach(roomId => {
    //console.log('deleting for real', roomId)
    //Create ref and destroy each in the list
    let roomToDeleteRef = window.firebase.database().ref(`rooms/${roomId}`);
    roomToDeleteRef.remove();
  });
}


/* Track turn activity in each room */
export function recordTurnActivityInRoom(turnKey:string, turnReport:{}) {

  if (!turnReport.actionId) {
    console.warn('actionId not defined in turnReport', turnReport)
  }

  let useTurnReport = {
    ...turnReport,
    actionId: turnReport.actionId || "unknown!"
  };

  window.roomRef.child("turnReporting").child(turnKey).set(useTurnReport)
}

export function writeTauntToRoom(tauntString:string) {
  if (window.roomRef) { //This is only available in Online play
    window.roomRef.child("taunt").set({
      timestamp: Date.now(),
      message: tauntString
    })
  }
}

export function updateCrewNameInRoom() {
  if (window.roomRef) {
    //Set my crew name
    window.roomRef
      .child("crewNames")
      .child(store.getState().battle.battlePlayerId[0])
      .set(store.getState().crew.name);
  }
}


// WEBPACK FOOTER //
// ./src/battle/connection/firebase-rooms.js